import { loadableReady } from '@loadable/component';
import * as React from 'react';
import { hydrateRoot } from 'react-dom/client';

import { IAppStore } from '../../../shared/common/state';
import { SsrMarkupFallback } from '../../../shared/components/SsrMarkupFallback';
import { Application } from '../../../shared/containers/App';
import { IApplicationContext } from '../../../shared/types/applicationContext';

interface IDependencies {
  store: IAppStore;
  context: IApplicationContext;
}

const toError = (error: unknown): Error => (error instanceof Error ? error : new Error(String(error)));

export function renderApplication(dependencies: IDependencies) {
  const { store, context } = dependencies;
  const { config, logger } = context;

  const projectName = config.getStrict<string>('projectName');
  const rootElement = document.getElementById(projectName as string);

  if (!rootElement) {
    throw new Error(`Unable to find element #${projectName}`);
  }

  /**
   * Снимок серверной разметки до гидрации. React 19 при ошибке гидрации очищает root
   * и перерендеривает его на клиенте, поэтому снимок можно взять только здесь.
   * Используется как заглушка корневого error boundary и для восстановления после uncaught error.
   */
  const ssrMarkup = rootElement.innerHTML;

  loadableReady(() => {
    hydrateRoot(
      rootElement,
      <Application context={context} reduxStore={store} errorStub={<SsrMarkupFallback html={ssrMarkup} />} />,
      {
        // Ошибка поймана error boundary (корневым или вложенным). Алерты настраиваются по domain.
        onCaughtError: (error, errorInfo) => {
          logger.error(toError(error), { domain: 'react.caughtError', componentStack: errorInfo.componentStack });
        },
        // Ошибка не поймана ни одним boundary: React размонтировал root. Возвращаем серверную разметку,
        // чтобы страница не осталась пустой.
        onUncaughtError: (error, errorInfo) => {
          logger.error(toError(error), { domain: 'react.uncaughtError', componentStack: errorInfo.componentStack });
          rootElement.innerHTML = ssrMarkup;
        },
        // Hydration mismatch и ошибки гидрации, после которых React сам перерендерил корень на клиенте.
        onRecoverableError: (error, errorInfo) => {
          logger.warning(toError(error), {
            domain: 'react.recoverableError',
            componentStack: errorInfo.componentStack,
          });
        },
      },
    );
  });
}
